import { useRef } from "react";
import type { StyleSpecification } from "maplibre-gl";
import { isModelConsistent } from "../../libs/simple/apply";
import { buildModel, structureSignature } from "../../libs/simple/model";
import type { SimpleModel } from "../../libs/simple/types";

type MapStyle = StyleSpecification & { id: string };

type Cache = { key: string; seen: MapStyle; model: SimpleModel };

// Keeps one model while the user only changes colours, so swatches stay put.
// Rebuilds when layers change, when a slot stops holding a single colour,
// and whenever a style arrives that the session did not produce itself
// (another style opened, an undo to before the session's first change, an
// edit made elsewhere).
export function useSimpleModel(
  style: MapStyle,
  session: { owns(style: MapStyle): boolean }
): SimpleModel {
  const cache = useRef<Cache | null>(null);
  const key = `${style.id || ""}|${structureSignature(style)}`;
  const current = cache.current;

  const foreign =
    current !== null && style !== current.seen && !session.owns(style);

  if (
    !current ||
    current.key !== key ||
    foreign ||
    !isModelConsistent(style, current.model)
  ) {
    cache.current = { key, seen: style, model: buildModel(style) };
  } else {
    current.seen = style;
  }
  return cache.current!.model;
}
